import { motion } from "framer-motion";
import { BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList
} from "recharts";
import { OPTION_COLORS } from "./constants";
import { CustomTooltip, Bar3D } from "./ChartComponents";
import { OPTION_COLORS_DARK, getTotalOptionsVoted, getChartData } from "./dataUtils";

export function GeneralTab({ results, options, optionColorMap, consulta }) {
  const { t } = useTranslation();
  const totalOptionsVoted = getTotalOptionsVoted(results);
  const chartData = getChartData(results, consulta, optionColorMap);

  return (
    <div className="space-y-6" data-testid="general-tab">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 className="h-5 w-5 text-slate-500" />
          <h2 className="text-lg font-heading font-semibold text-slate-900">{t('results.distributionVotes')}</h2>
        </div>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fill: '#374151', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} angle={-45} textAnchor="end" interval={0} height={100} />
              <YAxis tick={{ fill: '#374151', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar dataKey="vots" shape={<Bar3D />}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={optionColorMap[entry.name] || OPTION_COLORS[index]} />
                ))}
                <LabelList dataKey="vots" position="inside" fill="#ffffff" fontSize={14} fontWeight="bold" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }} />
                <LabelList dataKey="percentage" position="top" fill="#1e293b" fontSize={14} fontWeight="bold" offset={15}
                  content={({ x, y, width, value }) => (
                    <text x={x + width - 18} y={y - 15} fill="#1e293b" fontSize={14} fontWeight="bold" textAnchor="middle">{value}</text>
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-lg font-heading font-semibold text-slate-900 mb-6">{t('results.resultsSummary')}</h2>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('results.optionColumn')}</th>
                <th className="text-right">{t('results.votesColumn')}</th>
                <th className="text-right">{t('results.percentageColumn')}</th>
              </tr>
            </thead>
            <tbody>
              {[...options]
                .sort((a, b) => (results?.results?.[b] || 0) - (results?.results?.[a] || 0))
                .map((option) => {
                  const votes = results?.results?.[option] || 0;
                  const percentage = totalOptionsVoted > 0 ? ((votes / totalOptionsVoted) * 100).toFixed(2) : 0;
                  const originalIndex = options.indexOf(option);
                  return (
                    <tr key={option}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{
                            background: `linear-gradient(135deg, ${optionColorMap[option] || OPTION_COLORS[originalIndex]} 0%, ${OPTION_COLORS_DARK[originalIndex % OPTION_COLORS_DARK.length]} 100%)`,
                            boxShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                          }} />
                          <span className="font-medium">{option}</span>
                        </div>
                      </td>
                      <td className="text-right font-mono">{votes}</td>
                      <td className="text-right font-mono">{percentage}%</td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td className="font-semibold">{t('results.total')}</td>
                <td className="text-right font-mono font-semibold">{totalOptionsVoted}</td>
                <td className="text-right font-mono font-semibold">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
